/**
 * Documents page — list, upload, download, delete.
 *
 * Uploads are multipart/form-data with `file` + `metadata` (JSON). The server
 * parses `metadata` through `documentCreateInput` and stores the binary in R2.
 * confidentialRank badges reflect read scope (Public/Manager/Admin).
 */
import { useMemo, useRef, useState } from 'react';
import { FileText, Upload, Download, Trash2, Pencil } from 'lucide-react';
import { useDocuments, useUploadDocument, useUpdateDocument, useDeleteDocument } from '../api.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Select, Field } from '../../../shell/Field.js';
import { useEmployeeDirectory } from '../../employees/api.js';
import { EmployeeSelect, formatEmployeeOption } from '../../employees/components/EmployeeSelect.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { formatDateTime } from '../../../lib/format.js';
import { toast } from '../../../shell/Toast.js';
import { useAuthStore } from '../../../store/auth.js';
import {
  DOCUMENT_CATEGORY,
  CONFIDENTIAL_RANK,
  type DocumentCategory,
  type ConfidentialRank,
} from '@shared/enums/index.js';
import type { Document } from '@shared/types';

const rankTone: Record<ConfidentialRank, 'green' | 'yellow' | 'red'> = {
  Public: 'green',
  Manager: 'yellow',
  Admin: 'red',
};

const UPLOAD_CATEGORIES = DOCUMENT_CATEGORY.filter((c) => !['Avatar', 'Disciplinary', 'Conversation', 'Recognition', 'TimeOff'].includes(c));

export function DocumentsPage() {
  const { data, isLoading } = useDocuments();
  const { user } = useAuthStore();
  const { data: directory } = useEmployeeDirectory();
  const employeeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of directory?.items ?? []) {
      map.set(e.id, formatEmployeeOption(e));
    }
    return map;
  }, [directory]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Document | null>(null);
  const deleteDoc = useDeleteDocument();

  const canManage = user?.role === 'Admin' || user?.role === 'Manager';
  const isAdmin = user?.role === 'Admin';

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc.mutateAsync(id);
      toast('success', 'Document deleted.');
    } catch {
      toast('error', 'Failed to delete document.');
    }
  };

  const columns: Column<Document>[] = [
    { key: 'name', header: 'Name', render: (d) => <span className="font-medium text-slate-900 dark:text-slate-100">{d.name}</span> },
    {
      key: 'employee',
      header: 'Employee',
      render: (d) => (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {employeeLabel.get(d.employeeId) ?? 'Employee'}
        </span>
      ),
    },
    { key: 'category', header: 'Category', render: (d) => <Badge tone="blue">{d.category}</Badge> },
    {
      key: 'rank',
      header: 'Confidential',
      render: (d) => <Badge tone={rankTone[d.confidentialRank]}>{d.confidentialRank}</Badge>,
    },
    { key: 'size', header: 'Size', render: (d) => formatBytes(d.sizeBytes) },
    { key: 'uploaded', header: 'Uploaded', render: (d) => formatDateTime(d.uploadedAt) },
    {
      key: 'actions',
      header: '',
      render: (d) => (
        <div className="flex justify-end gap-1">
          {canManage && (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Edit ${d.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setEditTarget(d);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          <a
            href={`/api/documents/${d.id}/content`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex"
          >
            <Button size="sm" variant="ghost" aria-label={`Download ${d.name}`}>
              <Download className="h-4 w-4" />
            </Button>
          </a>
          {isAdmin && (
            <Button
              size="sm"
              variant="danger"
              aria-label={`Delete ${d.name}`}
              loading={deleteDoc.isPending}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(d.id);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Documents</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{data?.items.length ?? 0} files</p>
        </div>
        {canManage && (
          <Button onClick={() => setModalOpen(true)}>
            <Upload className="h-4 w-4" /> Upload
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
      ) : data && data.items.length > 0 ? (
        <Table columns={columns} data={data.items} />
      ) : (
        <EmptyState
          icon={<FileText className="h-8 w-8" />}
          title="No documents"
          description="Upload a document to get started."
        />
      )}

      <UploadModal open={modalOpen} onClose={() => setModalOpen(false)} />
      {editTarget && (
        <EditDocumentModal document={editTarget} onClose={() => setEditTarget(null)} />
      )}
    </div>
  );
}

function UploadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const upload = useUploadDocument();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [employeeId, setEmployeeId] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<DocumentCategory>('Other');
  const [rank, setRank] = useState<ConfidentialRank>('Manager');
  const [file, setFile] = useState<File | null>(null);

  const reset = () => {
    setName('');
    setFile(null);
    setEmployeeId('');
    setCategory('Other');
    setRank('Manager');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!file) return;
    try {
      await upload.mutateAsync({
        file,
        metadata: {
          employeeId,
          name: name.trim() || file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          category,
          confidentialRank: rank,
        },
      });
      toast('success', 'Document uploaded.');
      reset();
      onClose();
    } catch {
      toast('error', 'Upload failed.');
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Upload Document">
      <div className="space-y-4">
        <EmployeeSelect
          label="Employee"
          value={employeeId}
          onChange={setEmployeeId}
        />
        <Field label="Document name (optional)">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Defaults to the file name"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as DocumentCategory)}
          >
            {UPLOAD_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
          <Select
            label="Confidentiality"
            value={rank}
            onChange={(e) => setRank(e.target.value as ConfidentialRank)}
          >
            {CONFIDENTIAL_RANK.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </div>
        <Field label="File">
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200 dark:text-slate-400 file:dark:bg-slate-800 hover:file:dark:bg-slate-700"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} loading={upload.isPending} disabled={!employeeId || !file}>
            Upload
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditDocumentModal({
  document,
  onClose,
}: {
  document: Document;
  onClose: () => void;
}) {
  const update = useUpdateDocument();
  const [name, setName] = useState(document.name);
  const [category, setCategory] = useState<DocumentCategory>(document.category);
  const [rank, setRank] = useState<ConfidentialRank>(document.confidentialRank);

  const handleSubmit = async () => {
    try {
      await update.mutateAsync({
        id: document.id,
        body: { name: name.trim(), category, confidentialRank: rank },
      });
      toast('success', 'Document updated.');
      onClose();
    } catch {
      toast('error', 'Failed to update document.');
    }
  };

  return (
    <Modal open onClose={onClose} title="Edit Document">
      <div className="space-y-4">
        <Field label="Document name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as DocumentCategory)}
          >
            {UPLOAD_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
          <Select
            label="Confidentiality"
            value={rank}
            onChange={(e) => setRank(e.target.value as ConfidentialRank)}
          >
            {CONFIDENTIAL_RANK.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} loading={update.isPending} disabled={!name.trim()}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
